import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from 'react'

import produce from 'immer'
import { useNavigate } from 'react-router'
import {
  Template,
  TemplateStatus,
} from 'src/legacy_modules/entities/template/entity'
import { fetchList as fetchTemplates } from 'src/legacy_modules/entities/template/request'
import { TemplateFolder } from 'src/legacy_modules/entities/template-folder/entity'
import { fetchList as fetchTemplateFolders } from 'src/legacy_modules/entities/template-folder/request'
import { routes } from 'src/legacy_modules/routes'
import { assertNever } from 'src/utils/assertNever'

import { UpdateMediumItemOrderResponse } from './common/detail/request-update-order'

type State = {
  templates: Record<string, Template>
  templateFolders: Record<string, TemplateFolder>
  fetched: boolean
}

type Action =
  | {
      type: 'replaceAll'
      templates: Template[]
      templateFolders: TemplateFolder[]
    }
  | { type: 'upsertTemplate'; template: Template }
  | { type: 'deleteTemplate'; uid: string }
  | { type: 'releaseTemplate'; uid: string }
  | {
      type: 'updateMediumItemOrder'
      uid: string
      res: UpdateMediumItemOrderResponse[]
    }
  | { type: 'upsertTemplateFolder'; templateFolder: TemplateFolder }
  | { type: 'deleteTemplateFolder'; uid: string }
  | { type: 'sortTemplateFolders'; folders: TemplateFolder[] }

const initialState: State = {
  templates: {},
  templateFolders: {},
  fetched: false,
}

const reducer = (state: State, action: Action): State =>
  produce(state, draft => {
    switch (action.type) {
      case 'replaceAll': {
        // list API は mediumItems を返さないため、詳細取得済みの mediumItems を保持して
        // 一覧再取得で詳細の編集状態が消えないようにする
        const nextTemplates: Record<string, Template> = {}
        action.templates.forEach(t => {
          const existing = state.templates[t.uid]
          nextTemplates[t.uid] = {
            ...t,
            mediumItems: existing ? existing.mediumItems : [],
          }
        })
        draft.templates = nextTemplates

        const nextFolders: Record<string, TemplateFolder> = {}
        action.templateFolders.forEach(f => {
          nextFolders[f.uid] = f
        })
        draft.templateFolders = nextFolders

        draft.fetched = true
        return
      }
      case 'upsertTemplate':
        draft.templates[action.template.uid] = action.template
        return
      case 'deleteTemplate':
        delete draft.templates[action.uid]
        return
      case 'releaseTemplate': {
        const template = draft.templates[action.uid]
        if (template) template.status = TemplateStatus.Released
        return
      }
      case 'updateMediumItemOrder': {
        const template = draft.templates[action.uid]
        if (!template) return

        action.res.forEach(mediumItemOrderResponse => {
          const mediumItem = template.mediumItems.find(
            mi => mi.uid === mediumItemOrderResponse.medium_item_uid,
          )
          if (!mediumItem) return
          mediumItem.index = mediumItemOrderResponse.index

          mediumItemOrderResponse.small_items.forEach(smallItemResponse => {
            const smallItem =
              mediumItem.smallItemMap[smallItemResponse.small_item_uid]
            if (smallItem) smallItem.index = smallItemResponse.index
          })
        })

        template.mediumItems = template.mediumItems.sort(
          (a, b) => a.index - b.index,
        )
        return
      }
      case 'upsertTemplateFolder':
        draft.templateFolders[action.templateFolder.uid] = action.templateFolder
        return
      case 'deleteTemplateFolder':
        delete draft.templateFolders[action.uid]
        return
      case 'sortTemplateFolders':
        action.folders.forEach(f => {
          const folder = draft.templateFolders[f.uid]
          if (folder) folder.index = f.index
        })
        return
      default:
        return assertNever(action)
    }
  })

type ContextValue = {
  fetched: boolean
  templates: Template[]
  nonPresetFolders: TemplateFolder[]
  presetFolder: TemplateFolder | undefined
  getTemplate: (uid: string) => Template | undefined
  getTemplateFolder: (uid: string) => TemplateFolder | undefined
  getTemplatesByFolderUid: (folderUid: string) => Template[]
  upsertTemplate: (template: Template) => void
  deleteTemplate: (uid: string) => void
  releaseTemplate: (uid: string) => void
  updateMediumItemOrder: (
    uid: string,
    res: UpdateMediumItemOrderResponse[],
  ) => void
  upsertTemplateFolder: (templateFolder: TemplateFolder) => void
  deleteTemplateFolder: (uid: string) => void
  sortTemplateFolders: (folders: TemplateFolder[]) => void
}

const TemplateEntitiesContext = createContext<ContextValue | null>(null)

type ProviderProps = {
  trialUid: string
  children: React.ReactNode
}

export const TemplateEntitiesProvider: React.FC<ProviderProps> = ({
  trialUid,
  children,
}) => {
  const [state, dispatch] = useReducer(reducer, initialState)
  const navigate = useNavigate()

  useEffect(() => {
    let cancelled = false

    const run = async () => {
      try {
        const [templates, templateFolders] = await Promise.all([
          fetchTemplates({ trialUid }),
          fetchTemplateFolders({ trialUid }),
        ])
        if (cancelled) return
        dispatch({ type: 'replaceAll', templates, templateFolders })
      } catch (error) {
        if (cancelled) return
        console.error('TemplateEntitiesProvider fetch error: ', error)
        if (error?.original?.response?.status === 403) {
          navigate(routes.httpError403)
        } else {
          navigate(routes.requiredLogin)
        }
      }
    }

    run()

    return () => {
      cancelled = true
    }
  }, [trialUid, navigate])

  const templates = useMemo(
    () => Object.values(state.templates),
    [state.templates],
  )

  const nonPresetFolders = useMemo(
    () =>
      Object.values(state.templateFolders)
        .filter(f => !f.isPreset)
        .sort((a, b) => a.index - b.index),
    [state.templateFolders],
  )

  const presetFolder = useMemo(
    () => Object.values(state.templateFolders).find(f => f.isPreset),
    [state.templateFolders],
  )

  const getTemplate = useCallback(
    (uid: string) => state.templates[uid],
    [state.templates],
  )

  const getTemplateFolder = useCallback(
    (uid: string) => state.templateFolders[uid],
    [state.templateFolders],
  )

  const getTemplatesByFolderUid = useCallback(
    (folderUid: string) =>
      Object.values(state.templates)
        .filter(t => t.templateFolderUid === folderUid)
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        ),
    [state.templates],
  )

  const upsertTemplate = useCallback(
    (template: Template) => dispatch({ type: 'upsertTemplate', template }),
    [],
  )
  const deleteTemplate = useCallback(
    (uid: string) => dispatch({ type: 'deleteTemplate', uid }),
    [],
  )
  const releaseTemplate = useCallback(
    (uid: string) => dispatch({ type: 'releaseTemplate', uid }),
    [],
  )
  const updateMediumItemOrder = useCallback(
    (uid: string, res: UpdateMediumItemOrderResponse[]) =>
      dispatch({ type: 'updateMediumItemOrder', uid, res }),
    [],
  )
  const upsertTemplateFolder = useCallback(
    (templateFolder: TemplateFolder) =>
      dispatch({ type: 'upsertTemplateFolder', templateFolder }),
    [],
  )
  const deleteTemplateFolder = useCallback(
    (uid: string) => dispatch({ type: 'deleteTemplateFolder', uid }),
    [],
  )
  const sortTemplateFolders = useCallback(
    (folders: TemplateFolder[]) =>
      dispatch({ type: 'sortTemplateFolders', folders }),
    [],
  )

  const value = useMemo<ContextValue>(
    () => ({
      fetched: state.fetched,
      templates,
      nonPresetFolders,
      presetFolder,
      getTemplate,
      getTemplateFolder,
      getTemplatesByFolderUid,
      upsertTemplate,
      deleteTemplate,
      releaseTemplate,
      updateMediumItemOrder,
      upsertTemplateFolder,
      deleteTemplateFolder,
      sortTemplateFolders,
    }),
    [
      state.fetched,
      templates,
      nonPresetFolders,
      presetFolder,
      getTemplate,
      getTemplateFolder,
      getTemplatesByFolderUid,
      upsertTemplate,
      deleteTemplate,
      releaseTemplate,
      updateMediumItemOrder,
      upsertTemplateFolder,
      deleteTemplateFolder,
      sortTemplateFolders,
    ],
  )

  return (
    <TemplateEntitiesContext.Provider value={value}>
      {children}
    </TemplateEntitiesContext.Provider>
  )
}

export const useTemplateEntities = (): ContextValue => {
  const ctx = useContext(TemplateEntitiesContext)
  if (!ctx) {
    throw new Error(
      'useTemplateEntities must be used within TemplateEntitiesProvider',
    )
  }
  return ctx
}
